import { marked, type Token, type Tokens } from "marked";

import type { RetrievedChunkSnapshot } from "../db/types";
import { CITATION_RE, parseCitationMatch } from "./citations";
import { tr } from "../i18n/i18n.svelte";
import { base64ToBytes, DOCX_MIME, parseRevision, type DocxEdit, type DocxRevision } from "./docxEdits";
import type { EditedPdf } from "./pdfEdit";
import { docxTemplateRequest, fieldKey, fillSheetFields, loadTemplate, missingFieldsNote, withSignature, xlsxTemplateRequest } from "./docTemplates";

// Fichiers générés par le modèle (12.10.2026). Le modèle écrit le contenu
// dans un bloc ````file:nom.ext ; l'interface le remplace par une carte
// « Télécharger » et convertit le contenu selon l'extension, entièrement dans
// le navigateur. Rien n'est stocké : le fichier est reconstruit à partir du
// texte du message à chaque clic.

export interface FileBlock {
  /** Nom tel qu'écrit par le modèle, nettoyé pour le système de fichiers. */
  name: string;
  /** Extension en minuscules, sans le point ("pdf", "xlsx"…). */
  ext: string;
  body: string;
  /** Fence fermante reçue : faux tant que le bloc est en cours de streaming. */
  complete: boolean;
}

export type FileKind = "pdf" | "xlsx" | "doc" | "csv" | "text";

const KIND_BY_EXT: Record<string, FileKind> = {
  pdf: "pdf",
  xlsx: "xlsx",
  xls: "xlsx",
  doc: "doc",
  docx: "doc",
  csv: "csv",
  tsv: "text",
  md: "text",
  txt: "text",
  json: "text",
  ics: "text",
  xml: "text",
};

export function fileKind(ext: string): FileKind | null {
  return KIND_BY_EXT[ext] ?? null;
}

const FILE_LANG_RE = /^file:\s*(.+)$/i;

/** Blocs ```file:… du message, dans l'ordre du document (= ordre du rendu). */
export function extractFileBlocks(content: string): FileBlock[] {
  const out: FileBlock[] = [];
  marked.walkTokens(marked.lexer(content), (t) => {
    if (t.type !== "code") return;
    const m = FILE_LANG_RE.exec((t as Tokens.Code).lang ?? "");
    if (!m) return;
    const name = sanitizeFilename(m[1]);
    out.push({
      name,
      ext: extOf(name),
      body: (t as Tokens.Code).text,
      complete: isFenceClosed(t.raw),
    });
  });
  return out;
}

/** La fence fermante doit reprendre le caractère de l'ouvrante, au moins
 *  aussi long : un ``` intérieur (bloc mermaid dans un ````file:) ne ferme pas. */
function isFenceClosed(raw: string): boolean {
  const open = /^ {0,3}(`{3,}|~{3,})/.exec(raw);
  if (!open) return true; // bloc indenté : pas de fence, toujours complet
  const close = /\n {0,3}(`{3,}|~{3,})[ \t]*\n*$/.exec(raw);
  return !!close && close[1][0] === open[1][0] && close[1].length >= open[1].length;
}

export function sanitizeFilename(name: string): string {
  const clean = name
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/^[._]+/, "")
    .slice(0, 120);
  return clean || "fichier.txt";
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i + 1).toLowerCase() : "";
}

/** Bloc ````file:….docx en JSON : révision d'un Word fourni par l'usager,
 *  rendue dans son fichier d'origine en suivi des modifications. */
export function isDocxRevision(block: FileBlock): boolean {
  return block.ext === "docx" && parseRevision(block.body) !== null;
}

/** Bloc ````file:….pdf en JSON : modification d'un PDF fourni par l'usager,
 *  appliquée dans son fichier d'origine par le serveur. */
export function isPdfRevision(block: FileBlock): boolean {
  return block.ext === "pdf" && parseRevision(block.body) !== null;
}

/** Nom d'un document sans extension, casse ni séparateurs : le modèle nomme sa
 *  source comme il l'a lue. */
export function docStem(name: string): string {
  return name
    .toLowerCase()
    .replace(/\.(docx|pdf|xlsx|csv|md|txt)$/, "")
    .replace(/[\s_-]+/g, " ")
    .trim();
}

export interface RevisionChain {
  /** Document d'origine visé par la première révision de la chaîne. */
  source: string;
  /** Remplacements des révisions précédentes, de la plus ancienne à la plus
   *  récente, à rejouer sur l'original avant ceux du bloc. */
  earlier: DocxEdit[][];
}

/** Révisions qu'un bloc `"continue": true` prolonge. `conversation` : les blocs
 *  des réponses, dans l'ordre ; le bloc lui-même y figure (réponse enregistrée)
 *  ou pas encore (réponse en cours). Le modèle nomme la révision précédente
 *  par son document d'origine ou par le nom du fichier qu'il a rendu. Rien
 *  n'est stocké : la version précédente est reconstruite à chaque fois. */
export function revisionChain(block: FileBlock, conversation: FileBlock[]): RevisionChain {
  const current = parseRevision(block.body);
  if (!current) return { source: "", earlier: [] };
  const same = conversation.filter((b) => b.complete && b.ext === block.ext);
  let end = same.map((b) => b.body).lastIndexOf(block.body);
  if (end < 0) end = same.length;

  // Chaque révision d'avant, avec les noms sous lesquels on peut la
  // prolonger : l'original et chacun des fichiers rendus de sa lignée.
  interface Step {
    names: Set<string>;
    source: string;
    /** Remplacements à rejouer sur l'original, elle comprise. */
    edits: DocxEdit[][];
  }
  const steps: (Step | null)[] = [];
  const resolve = (r: DocxRevision, name: string): Omit<Step, "edits"> & { earlier: DocxEdit[][] } => {
    let prev: Step | null = null;
    if (r.continue) {
      const want = docStem(r.source);
      for (let i = steps.length - 1; i >= 0 && !prev; i--) if (steps[i]?.names.has(want)) prev = steps[i];
    }
    return {
      names: new Set([...(prev?.names ?? []), docStem(r.source), docStem(name)]),
      source: prev?.source ?? r.source,
      earlier: prev?.edits ?? [],
    };
  };
  for (const b of same.slice(0, end)) {
    const r = parseRevision(b.body);
    const s = r && resolve(r, b.name);
    steps.push(s && r ? { names: s.names, source: s.source, edits: [...s.earlier, r.edits] } : null);
  }
  const { source, earlier } = resolve(current, block.name);
  return { source, earlier };
}

// === Révision d'un fichier généré dans la conversation (03.11.2026) ==========
// « Change la signature », « corrige le 2e paragraphe » : plutôt que de tout
// réécrire (lent, et le texte dérive), le modèle donne la même révision JSON
// que pour un Word fourni, mais sur un fichier qu'il a produit plus haut. On
// rejoue la lignée sur son texte (markdown, ou JSON d'un classeur), puis le
// fichier se construit comme s'il avait été écrit en entier.

export interface GeneratedRevision {
  /** Le texte complet du fichier révisé. */
  text: string;
  /** Remplacements de ce bloc introuvables dans la version de départ. */
  failed: DocxEdit[];
}

/** Bloc JSON qui révise un fichier (fourni ou généré), quel que soit le format. */
export function isFileRevision(block: FileBlock): boolean {
  const r = parseRevision(block.body);
  return !!r && !!r.source;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Le passage, tel quel ou aux blancs et guillemets près. */
function findLoose(text: string, find: string): [number, number] | null {
  const at = text.indexOf(find);
  if (at >= 0) return [at, at + find.length];
  const words = find.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const pattern = words
    .map((w) => escapeRegExp(w).replace(/['’]/g, "['’]").replace(/["«»“”]/g, '["«»“”]'))
    .join("\\s+");
  const m = new RegExp(pattern).exec(text);
  return m ? [m.index, m.index + m[0].length] : null;
}

/** Une valeur d'en-tête YAML : sur plusieurs lignes, en bloc « | ». */
function yamlLine(key: string, value: string): string {
  return value.includes("\n") ? `${key}: |\n${value.split("\n").map((l) => `  ${l}`).join("\n")}` : `${key}: ${value}`;
}

/** Change les champs de l'en-tête « --- … --- » (le crée au besoin). */
function setFrontMatter(text: string, fields: Record<string, string>): string {
  const m = /^\s*---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(text);
  const lines = m ? m[1].split(/\r?\n/) : [];
  for (const [key, value] of Object.entries(fields)) {
    const k = fieldKey(key);
    const start = lines.findIndex((l) => {
      const kv = /^([^\s:][^:]*?)\s*:/.exec(l);
      return !!kv && fieldKey(kv[1]) === k;
    });
    const entry = yamlLine(key, value).split("\n");
    if (start < 0) {
      lines.push(...entry);
      continue;
    }
    let end = start + 1;
    while (end < lines.length && /^\s/.test(lines[end])) end++;
    lines.splice(start, end - start, ...entry);
  }
  const head = `---\n${lines.join("\n")}\n---\n`;
  return m ? head + text.slice(m[0].length) : head + text;
}

/** Change les clés d'un classeur JSON ; sinon l'en-tête du document. */
function setFields(text: string, fields: Record<string, string>): string {
  try {
    const data = JSON.parse(text) as unknown;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const obj = data as Record<string, unknown>;
      for (const [key, value] of Object.entries(fields)) {
        const existing = Object.keys(obj).find((k) => fieldKey(k) === fieldKey(key));
        obj[existing ?? key] = value;
      }
      return JSON.stringify(obj, null, 1);
    }
  } catch {
    /* pas du JSON : un document markdown */
  }
  return setFrontMatter(text, fields);
}

function applyRevision(base: string, r: DocxRevision): GeneratedRevision {
  let text = base;
  const failed: DocxEdit[] = [];
  for (const e of r.edits) {
    const at = findLoose(text, e.find);
    if (!at) failed.push(e);
    else text = text.slice(0, at[0]) + e.replace + text.slice(at[1]);
  }
  if (r.fields && Object.keys(r.fields).length > 0) text = setFields(text, r.fields);
  return { text, failed };
}

/** La révision d'un fichier que le modèle a lui-même produit plus haut dans la
 *  conversation, rejouée sur son texte ; null si la source n'en est pas un
 *  (un Word ou un PDF fourni : révisé dans son fichier d'origine). Même lignée
 *  que `revisionChain` : `continue` part de la dernière version, sinon de la
 *  première. */
export function generatedRevision(block: FileBlock, conversation: FileBlock[]): GeneratedRevision | null {
  const current = parseRevision(block.body);
  if (!current?.source) return null;
  const same = conversation.filter((b) => b.complete && b.ext === block.ext);
  let end = same.map((b) => b.body).lastIndexOf(block.body);
  if (end < 0) end = same.length;
  // Par nom de fichier : la première version de sa lignée, et la dernière.
  const first = new Map<string, string>();
  const latest = new Map<string, string>();
  const step = (b: FileBlock, r: DocxRevision): GeneratedRevision | null => {
    const want = docStem(r.source);
    const root = first.get(want);
    const base = r.continue ? latest.get(want) : root;
    if (base === undefined || root === undefined) return null;
    const res = applyRevision(base, r);
    for (const name of [want, docStem(b.name)]) {
      first.set(name, root);
      latest.set(name, res.text);
    }
    return res;
  };
  for (const b of same.slice(0, end)) {
    const r = parseRevision(b.body);
    if (!r) {
      first.set(docStem(b.name), b.body);
      latest.set(docStem(b.name), b.body);
    } else if (r.source) step(b, r);
  }
  return step(block, current);
}

/** Nom de la copie modifiée d'une révision Word : « nom (modifié).docx ». */
export function cleanCopyName(block: FileBlock): string {
  return downloadName(block).replace(/\.docx$/i, tr({ fr: " (modifié).docx", en: " (edited).docx" }));
}

/** Nom réellement téléchargé : un .docx créé est un vrai .docx depuis le
 *  2.11.2026 (spec §11.1 ; il était produit en .doc, du HTML lu par Word), un
 *  .xls devient un vrai .xlsx. */
export function downloadName(block: FileBlock): string {
  const base = block.name.slice(0, block.name.length - block.ext.length);
  if (block.ext === "xls") return `${base}xlsx`;
  return block.name;
}

/** Les citations [N: "…"] deviennent une référence lisible hors de l'app :
 *  « [contrat.pdf, p. 3] ». Les numéros inconnus restent tels quels. */
export function resolveCitations(
  text: string,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
): string {
  if (!retrieved || retrieved.length === 0) return text;
  const byNumber = new Map(retrieved.map((r) => [r.number, r]));
  return text.replace(new RegExp(CITATION_RE.source, "g"), (...args) => {
    const parsed = parseCitationMatch(args as unknown as RegExpMatchArray);
    const labels = parsed.refs
      .map((n) => byNumber.get(n))
      .filter((r): r is RetrievedChunkSnapshot => !!r)
      .map((r) => (!r.source || r.source === "local" ? `${r.docName}, p. ${r.page}` : r.docName));
    return labels.length > 0 ? `[${labels.join(" ; ")}]` : (args[0] as string);
  });
}

// === Tableaux ================================================================

/** Cellules d'une ligne de tableau markdown (les « \| » ne séparent pas). */
function pipeCells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/(?<!\\)\|$/, "").split(/(?<!\\)\|/);
}

const DELIMITER_ROW_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/** Ramène une ligne de données à `n` cellules. L'erreur du modèle porte sur
 *  les suites de cellules vides (co-requérants absents…) : c'est dans la plus
 *  longue qu'on ajoute ou retire des vides, pour que les colonnes qui suivent
 *  (durée, budget, résumé) retombent à leur place. À défaut, en fin de ligne. */
function fitRow(cells: string[], n: number): string[] {
  let start = -1;
  let len = 0;
  for (let i = 0; i < cells.length; ) {
    if (cells[i].trim()) { i++; continue; }
    let j = i;
    while (j < cells.length && !cells[j].trim()) j++;
    if (j - i > len) [start, len] = [i, j - i];
    i = j;
  }
  const out = [...cells];
  const diff = n - cells.length;
  if (diff > 0) out.splice(start >= 0 ? start + len : out.length, 0, ...Array<string>(diff).fill(""));
  else if (start >= 0 && len >= -diff) out.splice(start, -diff);
  else out.length = n;
  return out;
}

/** Tableaux larges (des dizaines de colonnes) : le modèle se trompe dans le
 *  compte des colonnes. Une ligne `|---|` qui ne concorde pas avec l'en-tête
 *  fait refuser tout le tableau par marked (GFM : il devient un paragraphe
 *  plein de « | ») ; une ligne de données trop courte ou trop longue décale
 *  les colonnes. On aligne tout sur l'en-tête. */
export function repairPipeTables(markdown: string): string {
  const lines = markdown.split("\n");
  for (let i = 1; i < lines.length; i++) {
    const header = lines[i - 1];
    // Un « --- » sans « | » reste un trait ou un titre souligné, pas un tableau.
    if (!lines[i].includes("|") || !DELIMITER_ROW_RE.test(lines[i]) || !header.includes("|")) continue;
    const n = pipeCells(header).length;
    if (pipeCells(lines[i]).length !== n) lines[i] = `|${"---|".repeat(n)}`;
    for (let j = i + 1; j < lines.length && lines[j].includes("|") && lines[j].trim(); j++) {
      const cells = pipeCells(lines[j]);
      if (cells.length !== n) lines[j] = `| ${fitRow(cells, n).map((c) => c.trim()).join(" | ")} |`;
    }
  }
  return lines.join("\n");
}

export interface PlainTable {
  /** Titre markdown le plus proche au-dessus du tableau (nom de feuille Excel). */
  title: string | null;
  rows: string[][];
}

/** Texte brut d'une suite de tokens inline (gras, liens, code… aplatis). */
export function inlineText(tokens: Token[] | undefined, fallback = ""): string {
  if (!tokens) return fallback;
  return tokens
    .map((t) => {
      if (t.type === "br") return "\n";
      if (t.type === "html") return t.raw.replace(/<[^>]*>/g, "");
      if ("tokens" in t && t.tokens) return inlineText(t.tokens);
      return "text" in t ? String(t.text) : t.raw;
    })
    .join("");
}

export function extractTables(markdown: string): PlainTable[] {
  const out: PlainTable[] = [];
  let title: string | null = null;
  for (const t of marked.lexer(markdown)) {
    if (t.type === "heading") title = inlineText(t.tokens, t.text).trim();
    if (t.type !== "table") continue;
    const tbl = t as Tokens.Table;
    out.push({
      title,
      rows: [
        tbl.header.map((c) => inlineText(c.tokens, c.text).trim()),
        ...tbl.rows.map((r) => r.map((c) => inlineText(c.tokens, c.text).trim())),
      ],
    });
  }
  return out;
}

function cellText(v: unknown): string {
  if (v === null || v === undefined) return "";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

function recordsToRows(records: unknown[]): string[][] {
  const objects = records.filter((r): r is Record<string, unknown> => !!r && typeof r === "object" && !Array.isArray(r));
  // Colonnes dans l'ordre de première apparition, sur toutes les lignes.
  const columns = [...new Set(objects.flatMap((o) => Object.keys(o)))];
  return [columns, ...objects.map((o) => columns.map((c) => cellText(o[c])))];
}

/** Données JSON (12.10.2026) : une liste d'objets `[{ "Colonne": valeur }]`,
 *  ou `{ "Feuille": [ … ] }` pour plusieurs feuilles. Chaque valeur porte le
 *  nom de sa colonne : contrairement à un tableau markdown, un modèle ne peut
 *  pas décaler les colonnes en comptant mal les « | » d'un tableau de 50
 *  colonnes. Null si le corps n'est pas ce JSON. */
export function tablesFromJson(body: string): PlainTable[] | null {
  const text = body.trim();
  if (!text.startsWith("[") && !text.startsWith("{")) return null;
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (Array.isArray(data)) return [{ title: null, rows: recordsToRows(data) }];
  if (data && typeof data === "object") {
    const sheets = Object.entries(data).filter((e): e is [string, unknown[]] => Array.isArray(e[1]));
    if (sheets.length > 0) return sheets.map(([title, records]) => ({ title, rows: recordsToRows(records) }));
    return [{ title: null, rows: recordsToRows([data]) }];
  }
  return null;
}

/** Tableaux d'un fichier .xlsx ou .csv : JSON de préférence, sinon les
 *  tableaux markdown du corps. */
export function dataTables(body: string): PlainTable[] {
  // Classeur au modèle EPFL : les données sont sous « feuilles » ou « lignes ».
  const request = xlsxTemplateRequest(body);
  if (request) body = request.data;
  return tablesFromJson(body) ?? extractTables(body);
}

// === CSV =====================================================================

/** Lignes d'un corps sans tableau markdown : CSV « ; », « , » ou tabulé, ou
 *  une colonne de texte en dernier recours. */
export function rowsFromRawText(body: string): string[][] {
  const lines = body.split(/\r?\n/).filter((l) => l.trim());
  const sep = [";", "\t", ","].find((c) => lines.some((l) => l.includes(c))) ?? null;
  return lines.map((l) => (sep ? l.split(sep).map((c) => c.trim().replace(/^"(.*)"$/, "$1")) : [l]));
}

function csvField(v: string): string {
  return /[\t"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Texte du CSV, séparé par des TABULATIONS. Excel prend comme séparateur
 *  celui de la langue de Windows (« , » en en-US, « ; » en fr-CH) : aucun
 *  caractère ne marche partout, sauf la tabulation dans un fichier UTF-16,
 *  qu'Excel ouvre en colonnes quelle que soit la langue (cf. encodeUtf16le). */
export function buildCsv(body: string): string {
  const tables = dataTables(body);
  const rows = tables.length === 0 ? [rowsFromRawText(body)] : tables.map((t) => t.rows);
  return rows.map((t) => t.map((r) => r.map(csvField).join("\t")).join("\r\n")).join("\r\n\r\n") + "\r\n";
}

/** UTF-16 little-endian avec BOM : l'encodage du « Texte Unicode » d'Excel. */
export function encodeUtf16le(text: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(2 + text.length * 2);
  bytes[0] = 0xff;
  bytes[1] = 0xfe;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    bytes[2 + 2 * i] = c & 0xff;
    bytes[3 + 2 * i] = c >> 8;
  }
  return bytes;
}

// === Construction et téléchargement ==========================================

const TEXT_MIME: Record<string, string> = {
  json: "application/json",
  ics: "text/calendar",
  xml: "application/xml",
  md: "text/markdown",
  tsv: "text/tab-separated-values",
};

export interface BuildOptions {
  /** Fichier d'origine (base64) d'une révision, d'après le nom donné par le modèle. */
  findOriginal?: (source: string, ext: "docx" | "pdf") => string | null;
  /** Blocs de fichier des réponses de la conversation, dans l'ordre : une
   *  révision qui prolonge la précédente la rejoue d'abord (revisionChain). */
  conversation?: () => FileBlock[];
  /** Applique les remplacements à un PDF (le serveur, api/pdfEdit.ts). */
  editPdf?: (original: Uint8Array, edits: DocxEdit[]) => Promise<EditedPdf>;
  /** Fichier produit, mais incomplet (remplacements introuvables). */
  onWarning?: (message: string) => void;
  /** Révision d'un Word : la copie modifiée, changements acceptés, plutôt que
   *  le suivi des modifications. */
  clean?: boolean;
}

const short = (s: string, max = 60) => (s.length > max ? `${s.slice(0, max - 3)}…` : s);

/** Pas de citations [N: "…"] dans le texte d'un document modifié. */
function withoutCitations(edits: DocxEdit[]): DocxEdit[] {
  return edits.map((e) => ({
    find: e.find,
    replace: e.replace.replace(new RegExp(CITATION_RE.source, "g"), "").replace(/ {2,}/g, " "),
  }));
}

async function buildRevisedDocx(block: FileBlock, opts: BuildOptions): Promise<Blob> {
  const revision = parseRevision(block.body)!;
  const chain = revisionChain(block, opts.conversation?.() ?? []);
  const original = opts.findOriginal?.(chain.source, "docx") ?? null;
  if (!original) {
    throw new Error(
      `le document Word d'origine « ${chain.source || "?"} » n'est pas disponible ici — joignez-le à nouveau (ou réimportez-le dans vos documents), puis redemandez la révision`,
    );
  }
  const edits = withoutCitations(revision.edits);
  const { reviseDocx } = await import("./docxRevise");
  // La version précédente : ses avertissements ont été donnés à son tour.
  let base = base64ToBytes(original);
  for (const earlier of chain.earlier) {
    const prev = reviseDocx(base, withoutCitations(earlier), { clean: opts.clean });
    if (prev.applied > 0) base = prev.bytes;
  }
  const res = reviseDocx(base, edits, { clean: opts.clean });
  if (res.applied === 0) throw new Error("aucune des modifications n'a pu être appliquée au document d'origine");
  const quote = (list: { find: string }[]) => list.map((e) => `« ${short(e.find)} »`).join(", ");
  const notes: string[] = [];
  if (res.failed.length > 0) {
    // Avec le passage le plus proche : on voit si le modèle a mal relu.
    const missed = res.failed
      .map((e) => {
        const near = res.closest.get(e);
        return `« ${short(e.find, 140)} »${near ? ` (le document dit : « ${short(near.text, 140)} »)` : ""}`;
      })
      .join(", ");
    notes.push(`${res.failed.length} non appliquée${res.failed.length > 1 ? "s" : ""}, texte introuvable dans le document : ${missed}`);
  }
  // Le document dit déjà ce que propose le modèle : correction annoncée,
  // mais rien à changer.
  if (res.unchanged.length > 0) {
    notes.push(`${res.unchanged.length} sans effet, le document dit déjà la même chose : ${quote(res.unchanged)}`);
  }
  if (res.approximate.length > 0) {
    notes.push(`${res.approximate.length} placée${res.approximate.length > 1 ? "s" : ""} à quelques caractères près, à relire : ${quote(res.approximate)}`);
  }
  if (notes.length > 0) {
    opts.onWarning?.(`${block.name} : ${res.applied} modification${res.applied > 1 ? "s" : ""} appliquée${res.applied > 1 ? "s" : ""} sur ${edits.length} ; ${notes.join(" ; ")}`);
  }
  return new Blob([res.bytes], { type: DOCX_MIME });
}

async function buildEditedPdf(block: FileBlock, opts: BuildOptions): Promise<Blob> {
  const revision = parseRevision(block.body)!;
  const chain = revisionChain(block, opts.conversation?.() ?? []);
  const original = opts.findOriginal?.(chain.source, "pdf") ?? null;
  if (!original) {
    throw new Error(
      `le PDF d'origine « ${chain.source || "?"} » n'est pas disponible ici — joignez-le à nouveau (ou réimportez-le dans vos documents), puis redemandez la modification`,
    );
  }
  if (!opts.editPdf) throw new Error("modification de PDF indisponible ici");
  const edits = withoutCitations(revision.edits);
  let base = base64ToBytes(original);
  for (const earlier of chain.earlier) base = (await opts.editPdf(base, withoutCitations(earlier))).bytes;
  const { bytes, report } = await opts.editPdf(base, edits);
  const missing = report.filter((r) => r.count === 0);
  if (missing.length === report.length) throw new Error("aucun des passages à modifier n'a été trouvé dans le PDF d'origine");
  const notes: string[] = [];
  if (missing.length > 0) {
    notes.push(
      `${missing.length} non appliquée${missing.length > 1 ? "s" : ""}, texte introuvable dans le PDF : ${missing.map((r) => `« ${short(r.find, 140)} »`).join(", ")}`,
    );
  }
  const squeezed = report.filter((r) => r.squeezed);
  if (squeezed.length > 0) {
    notes.push(
      `${squeezed.length} resserrée${squeezed.length > 1 ? "s" : ""} pour tenir à la place de l'original, à relire : ${squeezed.map((r) => `« ${short(r.find)} »`).join(", ")}`,
    );
  }
  if (notes.length > 0) {
    const done = report.length - missing.length;
    opts.onWarning?.(`${block.name} : ${done} modification${done > 1 ? "s" : ""} appliquée${done > 1 ? "s" : ""} sur ${report.length} ; ${notes.join(" ; ")}`);
  }
  return new Blob([bytes], { type: "application/pdf" });
}

/** Champs d'un modèle EPFL restés à leur texte d'exemple : l'usager les complète. */
function warnMissing(block: FileBlock, keys: string[], opts: BuildOptions): void {
  if (keys.length === 0) return;
  opts.onWarning?.(`${block.name} : à compléter dans le fichier, PolyChat ne les connaissait pas — ${missingFieldsNote([...new Set(keys)])}`);
}

/** Écrit au nom d'une personne que l'annuaire EPFL ne connaît pas (ou pas
 *  seule de ce nom) : l'unité reste celle de l'usager, sans ses coordonnées. */
function warnUnknownSigner(block: FileBlock, name: string, opts: BuildOptions): void {
  if (!name) return;
  opts.onWarning?.(
    `${block.name} : « ${name} » est introuvable dans l'annuaire EPFL (ou plusieurs personnes portent ce nom) — vérifiez l'unité, la fonction et les coordonnées dans le fichier`,
  );
}

/** Construit le fichier d'un bloc. Les convertisseurs lourds (pdfmake,
 *  mermaid, la révision des .docx) ne sont chargés qu'au premier clic. */
export async function buildFile(
  block: FileBlock,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
  opts: BuildOptions = {},
): Promise<Blob> {
  // Révision d'un fichier généré plus haut : le texte révisé, construit comme
  // un fichier écrit en entier.
  const generated = isFileRevision(block) ? generatedRevision(block, opts.conversation?.() ?? []) : null;
  if (generated) {
    if (generated.failed.length > 0) {
      const n = generated.failed.length;
      opts.onWarning?.(
        `${block.name} : ${n} modification${n > 1 ? "s" : ""} non appliquée${n > 1 ? "s" : ""}, texte introuvable dans la version précédente : ${generated.failed.map((e) => `« ${short(e.find, 140)} »`).join(", ")}`,
      );
    }
    return buildFile({ ...block, body: generated.text }, retrieved, opts);
  }
  if (isDocxRevision(block)) return buildRevisedDocx(block, opts);
  if (isPdfRevision(block)) return buildEditedPdf(block, opts);
  const body = repairPipeTables(resolveCitations(block.body, retrieved));
  const title = block.name.replace(/\.[^.]*$/, "").replace(/[_-]+/g, " ");
  switch (fileKind(block.ext)) {
    case "pdf": {
      const { markdownToPdf } = await import("./filePdf");
      return markdownToPdf(body, title);
    }
    case "xlsx": {
      const { buildXlsx, sheetPageFromTemplate } = await import("./fileXlsx");
      const request = xlsxTemplateRequest(body);
      if (!request) return buildXlsx(body);
      // Classeur au modèle EPFL : sa mise en page, son en-tête et son pied de
      // page, aux coordonnées de l'annuaire pour ce que le modèle n'a pas donné.
      const { resolveFields } = await import("./epflDirectory.svelte");
      const { fields, unknown } = await resolveFields(request.fields);
      warnUnknownSigner(block, unknown, opts);
      const missing: string[] = [];
      const page = sheetPageFromTemplate(await loadTemplate(request.template), (xml) => {
        const filled = fillSheetFields(xml, fields);
        missing.push(...filled.defaults);
        return filled.xml;
      });
      warnMissing(block, missing, opts);
      return buildXlsx(request.data, page);
    }
    case "doc": {
      const request = block.ext === "docx" ? docxTemplateRequest(body) : null;
      if (request) {
        const [{ fillDocxTemplate }, { resolveFields }] = await Promise.all([import("./docTemplates"), import("./epflDirectory.svelte")]);
        const { fields, unknown } = await resolveFields(request.fields);
        warnUnknownSigner(block, unknown, opts);
        const text = withSignature(request.template, request.fields, fields, request.body);
        const filled = fillDocxTemplate(await loadTemplate(request.template), fields, text);
        warnMissing(block, filled.defaults, opts);
        return new Blob([filled.bytes as Uint8Array<ArrayBuffer>], { type: DOCX_MIME });
      }
      if (block.ext === "docx") {
        const { markdownToDocx } = await import("./docxExport");
        return new Blob([markdownToDocx(body) as Uint8Array<ArrayBuffer>], { type: DOCX_MIME });
      }
      const { markdownToWordDoc } = await import("./exportConversation");
      return markdownToWordDoc(body, title);
    }
    case "csv":
      return new Blob([encodeUtf16le(buildCsv(body))], { type: "text/csv;charset=utf-16le" });
    case "text":
      return new Blob([body.endsWith("\n") ? body : body + "\n"], {
        type: `${TEXT_MIME[block.ext] ?? "text/plain"};charset=utf-8`,
      });
    default:
      throw new Error(`Format .${block.ext || "?"} non pris en charge`);
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
